import React, { useRef, useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Box, Typography, Chip } from '@material-ui/core';
import { StorefrontRounded } from '@material-ui/icons';

// ── Toolbar configurations ──────────────────────────────────────────────────

const TOOLBAR_SUMMARY = [
    [{ header: [2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
];

// La barre ne propose que ce que l'assainissement (jsx/utils/ecomHtml) laisse passer : ni vidéo
// (iframe), ni couleurs (attribut style), ni retrait (classe), ni bouton image (il insère une
// image en data:, alors que seules les adresses http(s) sont admises).
const TOOLBAR_DESCRIPTION = [
    [{ header: [2, 3, 4, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['blockquote', 'code-block'],
    ['clean'],
];

// ── Styles injectés une seule fois ──────────────────────────────────────────

const QUILL_STYLES = `
.ecom-rich-editor .ql-container {
    font-size: 14px;
    font-family: inherit;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
}
.ecom-rich-editor .ql-toolbar {
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    background: #fafafa;
}
.ecom-rich-editor .ql-editor {
    min-height: 120px;
    max-height: 420px;
    overflow-y: auto;
}
.ecom-rich-editor.ecom-rich-editor--description .ql-editor {
    min-height: 200px;
}
.ecom-rich-editor .ql-editor p,
.ecom-rich-editor .ql-editor li {
    line-height: 1.6;
}
`;

let stylesInjected = false;
function injectStyles() {
    if (stylesInjected) return;
    const style = document.createElement('style');
    style.textContent = QUILL_STYLES;
    document.head.appendChild(style);
    stylesInjected = true;
}

// ── Compteur de caractères (HTML strippé) ───────────────────────────────────

function stripHtml(html) {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ');
}

// ── Composant principal ─────────────────────────────────────────────────────

/**
 * EcomRichEditor — éditeur riche pour les champs e-commerce (PrestaShop).
 *
 * Props :
 *   label       {string}   — label affiché au-dessus
 *   value       {string}   — HTML courant
 *   onChange    {fn}       — fn(htmlString) appelée à chaque changement
 *   placeholder {string}
 *   variant     {'summary'|'description'}  — 'summary' = toolbar réduite
 *   maxLen      {number}   — longueur max de texte (affichage seulement)
 */
export default function EcomRichEditor({
    label,
    value,
    onChange,
    placeholder,
    variant = 'description',
    maxLen,
}) {
    injectStyles();

    const quillRef = useRef(null);
    const isSummary = variant === 'summary';

    const modules = useMemo(() => ({
        toolbar: isSummary ? TOOLBAR_SUMMARY : TOOLBAR_DESCRIPTION,
    }), [isSummary]);

    const formats = isSummary
        ? ['header', 'bold', 'italic', 'underline', 'list', 'bullet', 'link']
        : [
            'header',
            'bold', 'italic', 'underline', 'strike',
            'list', 'bullet',
            'link', 'image',
            'blockquote', 'code-block',
        ];

    const textLength = stripHtml(value).trim().length;
    const isOverLimit = maxLen && textLength > maxLen;

    return (
        <Box mb={2}>
            {/* Label */}
            <Box display="flex" alignItems="center" mb={0.5} style={{ gap: 6 }}>
                <StorefrontRounded style={{ fontSize: 16, color: '#e91e63' }} />
                <Typography variant="caption" style={{ fontWeight: 600, color: '#555', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {label}
                </Typography>
                <Chip
                    label="E-commerce"
                    size="small"
                    style={{ fontSize: 10, height: 18, background: '#fce4ec', color: '#c2185b' }}
                />
            </Box>

            {/* Éditeur */}
            <div className={`ecom-rich-editor${isSummary ? '' : ' ecom-rich-editor--description'}`}>
                <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={value || ''}
                    onChange={onChange}
                    modules={modules}
                    formats={formats}
                    placeholder={placeholder || `Rédigez votre ${label?.toLowerCase() || 'contenu'}…`}
                />
            </div>

            {/* Compteur */}
            <Box display="flex" justifyContent="flex-end" mt={0.5}>
                <Typography
                    variant="caption"
                    style={{ color: isOverLimit ? '#d32f2f' : '#999' }}
                >
                    {textLength} caractère{textLength !== 1 ? 's' : ''}
                    {maxLen ? ` / ${maxLen} max` : ''}
                </Typography>
            </Box>
        </Box>
    );
}
